import update from 'immutability-helper'
import { createAction } from 'redux-actions'
import { ActionWithPayload, SnackbarLevel } from 'types'
import { Action } from 'redux'
import { isPrerendering } from 'services/prerender'
import { getBuildID } from 'services/versioning'

export const TOGGLE_SNACKBAR = 'flexhire/common/TOGGLE_SNACKBAR' as const
export const CLOSE_SNACKBAR = 'flexhire/common/CLOSE_SNACKBAR' as const
export const SET_ONLINE = 'flexhire/common/SET_ONLINE' as const
export const SET_OFFLINE = 'flexhire/common/SET_OFFLINE' as const
export const PROMPT_PWA_INSTALL = 'flexhire/common/PROMPT_PWA_INSTALL' as const
export const SET_IS_PWA = 'flexhire/common/SET_IS_PWA' as const
export const PWA_INSTALLED = 'flexhire/common/PWA_INSTALLED' as const
export const OPEN_MASK_DIALOG = 'flexhire/common/OPEN_MASK_DIALOG' as const
export const CLOSE_MASK_DIALOG = 'flexhire/common/CLOSE_MASK_DIALOG' as const
export const SET_REDIRECT_DIALOG_TARGET = 'flexhire/common/SET_REDIRECT_DIALOG_TARGET' as const
export const FRONTEND_VERSION_DEPLOYED = 'flexhire/common/FRONTEND_VERSION_DEPLOYED' as const
export const SERVICE_UNAVAILABLE = 'flexhire/common/SERVICE_UNAVAILABLE' as const

export const toggleSnackbar = createAction(TOGGLE_SNACKBAR) as ({ message, open }: { message?: string, open?: boolean, level?: SnackbarLevel })
 => ActionWithPayload<typeof TOGGLE_SNACKBAR, { message?: string, open?: boolean, level?: SnackbarLevel }>
export const closeSnackbar = createAction(CLOSE_SNACKBAR) as () => Action<typeof CLOSE_SNACKBAR>
export const setOnline = createAction(SET_ONLINE) as () => Action<typeof SET_ONLINE>
export const setOffline = createAction(SET_OFFLINE) as () => Action<typeof SET_OFFLINE>
export const promptPWAInstall = createAction(PROMPT_PWA_INSTALL) as () => Action<typeof PROMPT_PWA_INSTALL>
export const setIsPWA = createAction(SET_IS_PWA) as (isPwa: boolean) => ActionWithPayload<typeof SET_IS_PWA, boolean>
export const pwaInstalled = createAction(PWA_INSTALLED) as () => Action<typeof PWA_INSTALLED>
export const openMaskDialog = createAction(OPEN_MASK_DIALOG) as () => Action<typeof OPEN_MASK_DIALOG>
export const closeMaskDialog = createAction(CLOSE_MASK_DIALOG) as () => Action<typeof CLOSE_MASK_DIALOG>
export const setRedirectDialogTarget = createAction(SET_REDIRECT_DIALOG_TARGET) as (params: { url: Promise<string>, mode: string }) => ActionWithPayload<typeof SET_REDIRECT_DIALOG_TARGET, { url: Promise<string>, mode: string }>

const initialState = {
  snackbar: {
    open: false,
    message: '',
    level: 'info' as SnackbarLevel,
  },
  globalError: null as string | null,
  online: true,
  pwaInstallable: false,
  updateAvailable: false,
  userDelayedUpdate: false,
  serverBuildId: isPrerendering() ? getBuildID() : null,
  isPWA: false,
  maskDialogOpen: false,
  redirectDialogOpenFor: {
    mode: 'redirect',
    url: null as Promise<string> | null,
  },
}

type ActionType = ReturnType<
  typeof closeMaskDialog | typeof openMaskDialog | typeof pwaInstalled
  | typeof promptPWAInstall | typeof setIsPWA | typeof setOffline | typeof setOnline | typeof toggleSnackbar
  | typeof closeSnackbar | typeof setRedirectDialogTarget
>
  | ActionWithPayload<typeof FRONTEND_VERSION_DEPLOYED, { buildId: string }>
  | ActionWithPayload<typeof SERVICE_UNAVAILABLE, { message?: string }>

export default function reducer(state = initialState, action: ActionType) {
  switch (action.type) {
    case TOGGLE_SNACKBAR:
      return update(state, {
        snackbar: {
          open: { $set: action.payload.message ? true : (action.payload.open || false) },
          message: { $set: action.payload.message || '' },
          level: { $set: action.payload.level || 'info' as SnackbarLevel },
        },
      })
    case CLOSE_SNACKBAR:
      return update(state, {
        snackbar: {
          open: { $set: false },
          message: { $set: '' },
          level: { $set: 'info' },
        },
      })
    case SET_ONLINE:
      return update(state, {
        online: { $set: true },
      })
    case SET_OFFLINE:
      return update(state, {
        online: { $set: false },
      })
    case PROMPT_PWA_INSTALL:
      return update(state, {
        pwaInstallable: { $set: true },
      })
    case PWA_INSTALLED:
      return update(state, {
        pwaInstallable: { $set: false },
      })
    case SET_IS_PWA:
      return update(state, {
        isPWA: { $set: action.payload },
      })
    case OPEN_MASK_DIALOG:
      return update(state, {
        maskDialogOpen: { $set: true },
      })
    case CLOSE_MASK_DIALOG:
      return update(state, {
        maskDialogOpen: { $set: false },
      })
    case SET_REDIRECT_DIALOG_TARGET:
      return update(state, {
        redirectDialogOpenFor: { $set: action.payload },
      })
    case FRONTEND_VERSION_DEPLOYED:
      return update(state, {
        serverBuildId: { $set: action.payload?.buildId },
      })
    case SERVICE_UNAVAILABLE:
      return update(state, {
        globalError: { $set: action.payload?.message || 'Flexhire is unavailable at this time. If this issue persists, contact us at info@flexhire.com' },
      })
    default:
      return state
  }
}
